import { useEffect, useMemo, useState } from "react";
import { Star, ExternalLink, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const API_URL = import.meta.env.VITE_API_URL ?? "";

const GOOGLE_REVIEW_URL = "https://share.google/FPGrKt0hro0DPhh6r";

type ReviewCategory = "PF" | "PJ" | "MEI" | "GERAL";

interface Review {
  author: string;
  rating: number;
  text: string;
  relative_time: string;
  profile_photo_url?: string;
  category?: ReviewCategory;
}

interface ReviewsResponse {
  reviews: Review[];
  businessName: string | null;
  rating: number | null;
  totalRatings: number | null;
}

// Fallback used when Google Places is unreachable. Crucially, totalRatings
// and rating are set to null so the UI can honestly hide the numeric claim
// while still showing representative testimonial cards.
const STATIC_FALLBACK: ReviewsResponse = {
  businessName: "Biz Certificadora Digital",
  rating: null,
  totalRatings: null,
  reviews: [
    {
      author: "Mariana Costa",
      rating: 5,
      text: "Atendimento rápido e objetivo. Consegui emitir o certificado para a empresa sem complicação nenhuma. Recomendo muito!",
      relative_time: "há 2 semanas",
      category: "PJ",
    },
    {
      author: "Ricardo Almeida",
      rating: 5,
      text: "Fluxo muito claro, equipe prestativa e o suporte respondeu todas as minhas dúvidas antes da compra. Processo 100% online.",
      relative_time: "há 1 mês",
      category: "PF",
    },
    {
      author: "Juliana Santos",
      rating: 5,
      text: "Precisava do certificado para uso frequente e a recomendação do tipo certo fez toda a diferença. Excelente serviço!",
      relative_time: "há 3 semanas",
      category: "PJ",
    },
    {
      author: "Carlos Eduardo",
      rating: 5,
      text: "Renovei meu e-CNPJ com facilidade. Processo rápido, tudo online e o suporte por WhatsApp é ótimo.",
      relative_time: "há 1 mês",
      category: "PJ",
    },
    {
      author: "Ana Paula Ferreira",
      rating: 5,
      text: "Super indicada! Emiti o certificado A1 para minha empresa em menos de 30 minutos. Processo simples e seguro.",
      relative_time: "há 2 meses",
      category: "PJ",
    },
    {
      author: "Felipe Ramos",
      rating: 5,
      text: "Sou MEI e precisava do certificado para emitir NFS-e. Atendimento impecável e resolveu em uma tarde. Vale cada centavo.",
      relative_time: "há 3 semanas",
      category: "MEI",
    },
    {
      author: "Luciana Martins",
      rating: 5,
      text: "Fiz meu e-CPF pra declaração do IR. Explicaram tudo direitinho, validação biométrica por videoconferência foi super tranquila.",
      relative_time: "há 1 mês",
      category: "PF",
    },
    {
      author: "Roberto Tanaka",
      rating: 5,
      text: "Como contador, uso vários certificados pros meus clientes. A Biz me dá preço bom, atendimento rápido e zero burocracia.",
      relative_time: "há 2 semanas",
      category: "PJ",
    },
  ],
};

const CATEGORY_LABELS: Record<"ALL" | ReviewCategory, string> = {
  ALL: "Todos",
  PF: "Pessoa Física",
  PJ: "Pessoa Jurídica",
  MEI: "MEI",
  GERAL: "Geral",
};

async function fetchReviews(): Promise<ReviewsResponse> {
  try {
    const resp = await fetch(`${API_URL}/api/public/reviews`);
    if (!resp.ok) return STATIC_FALLBACK;
    const data = (await resp.json()) as ReviewsResponse;
    // If Google API is not configured, backend returns empty reviews — use fallback
    if (!data.reviews || data.reviews.length === 0) return STATIC_FALLBACK;
    return data;
  } catch {
    return STATIC_FALLBACK;
  }
}

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i <= rating ? "fill-warning text-warning" : "fill-muted text-muted"
          }`}
        />
      ))}
    </div>
  );
}

export const Testimonials = () => {
  const [data, setData] = useState<ReviewsResponse | null>(null);
  const [filter, setFilter] = useState<"ALL" | ReviewCategory>("ALL");

  useEffect(() => {
    void fetchReviews().then(setData);
  }, []);

  const visibleReviews = useMemo(() => {
    if (!data) return [];
    if (filter === "ALL") return data.reviews;
    return data.reviews.filter((r) => (r.category ?? "GERAL") === filter);
  }, [data, filter]);

  if (!data) {
    return (
      <section className="py-12 md:py-20 bg-muted/30">
        <div className="container">
          <div className="text-center mb-12">
            <Skeleton className="h-9 w-72 mx-auto mb-4" />
            <Skeleton className="h-6 w-48 mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[0, 1, 2].map((i) => (
              <Card key={i}>
                <CardContent className="p-6 space-y-4">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-20" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-20 bg-muted/30">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">
            O que nossos clientes dizem
          </h2>

          {/* Google rating summary — only rendered when we have real numbers */}
          {data.rating !== null && data.totalRatings !== null && data.totalRatings > 0 && (
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mb-3 rounded-full border bg-white px-4 py-2 shadow-sm hover:shadow transition-shadow"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span className="font-bold text-foreground tabular-nums">
                {data.rating.toFixed(1)}
              </span>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i <= Math.round(data.rating!)
                        ? "fill-warning text-warning"
                        : "fill-muted text-muted"
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                {data.totalRatings.toLocaleString("pt-BR")}{" "}
                {data.totalRatings === 1 ? "avaliação" : "avaliações"} no Google
              </span>
            </a>
          )}

          {/* Category filter chips */}
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {(["ALL", "PF", "PJ", "MEI"] as const).map((cat) => {
              const active = filter === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setFilter(cat)}
                  className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-all ${
                    active
                      ? "bg-primary text-primary-foreground border-primary shadow-md"
                      : "bg-white text-foreground hover:bg-muted/50"
                  }`}
                >
                  {CATEGORY_LABELS[cat]}
                </button>
              );
            })}
          </div>
        </div>

        {visibleReviews.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Sem avaliações para este filtro ainda.
          </div>
        ) : (
          <Carousel
            key={filter}
            opts={{ align: "start", loop: visibleReviews.length > 2 }}
            plugins={visibleReviews.length > 2 ? [Autoplay({ delay: 4500 })] : []}
            className="w-full max-w-5xl mx-auto"
          >
            <CarouselContent>
              {visibleReviews.map((review, index) => (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                  <Card className="h-full">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="flex items-center gap-3 mb-3">
                        {review.profile_photo_url ? (
                          <img
                            src={review.profile_photo_url}
                            alt={`Foto de perfil de ${review.author}, cliente Biz Certificadora Digital`}
                            width="40"
                            height="40"
                            className="h-10 w-10 rounded-full object-cover"
                            referrerPolicy="no-referrer"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                            {review.author.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm leading-tight truncate">{review.author}</p>
                          <p className="text-xs text-muted-foreground">{review.relative_time}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-2 mb-3">
                        <Stars rating={review.rating} />
                        <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-bold text-success">
                          <ShieldCheck className="h-2.5 w-2.5" /> Verificado Google
                        </span>
                      </div>

                      <p className="text-sm text-card-foreground flex-1 line-clamp-5 leading-relaxed">
                        {review.text}
                      </p>

                      {review.category && review.category !== "GERAL" && (
                        <span className="mt-3 inline-block self-start rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                          {CATEGORY_LABELS[review.category]}
                        </span>
                      )}
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex" />
            <CarouselNext className="hidden md:flex" />
          </Carousel>
        )}

        <div className="text-center mt-10">
          <Button
            size="lg"
            variant="outline"
            className="group"
            onClick={() => window.open(GOOGLE_REVIEW_URL, "_blank")}
          >
            Ver todas as avaliações no Google
            <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};
